import { DeleteOutlined } from '@ant-design/icons';
import { AuthRoles, AuthSubscriptions } from '@hefe/auth';
import { t } from '@hefe/i18n';
import { User } from '@hefe/models';
import {
  BasketballUsersSlice,
  currentUserSelector,
  UsersSlice,
} from '@hefe/store';
import { clubHasSubscription, formatDate, userHasRole } from '@hefe/utils';
import {
  Button,
  Col,
  Popconfirm,
  Row,
  Space,
  Tabs,
  Tooltip,
  Typography,
} from 'antd';
import { useAppDispatch } from 'apps/hefe-web/src/app/store/store';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import CrudPage from '../../../core/crud/components/crud-page.component';
import { useCrud } from '../../../core/crud/hooks/use-crud';
import {
  UserEmailAddress,
  UserEmailAddressCrud,
} from '../../../core/crud/resources/user-email-address.crud';
import AddEditTerminationForm from '../../forms/add-edit-termination/add-edit-termination.form';
import AddEditUserForm from '../../forms/add-edit-user/add-edit-user.form';
import EditUserRolesForm from '../../forms/edit-user-roles/edit-user-roles.form';
import LinkedUserAccountsForm from '../../forms/linked-user-accounts/linked-user-accounts.form';

interface EditUserComponentProps {
  user: User;
}

const emailAddressesSignature = (
  emailAddresses?: UserEmailAddress[]
): string => {
  if (!emailAddresses || emailAddresses.length === 0) {
    return '';
  }

  return emailAddresses
    .map(
      (item) =>
        `${item.id}|${item.user_id}|${item.email}|${item.label || ''}|${
          item.receive_notifications ? '1' : '0'
        }|${item.updated_at || ''}`
    )
    .sort()
    .join('||');
};

export default function EditUserComponent(props: EditUserComponentProps) {
  const authUser = useSelector(currentUserSelector);

  const dispatch = useAppDispatch();
  const [isSaving, setSaving] = useState(false);
  const userIsAdmin = userHasRole(AuthRoles.bbadmin, authUser);
  const clubHasFirstSubscription = clubHasSubscription(
    AuthSubscriptions.first,
    authUser
  );

  const users = useSelector(BasketballUsersSlice.basketballUsersSelector);

  const selectedUser = useMemo(() => {
    return users.find((u) => u.id === props.user.id);
  }, [users, props.user.id]);

  const userEmailAddressesCrud = useCrud<UserEmailAddress>(
    UserEmailAddressCrud,
    selectedUser
      ? {
          userId: selectedUser.id,
        }
      : undefined,
    undefined,
    {
      skipList: !selectedUser,
    }
  );

  useEffect(() => {
    if (!selectedUser) {
      return;
    }

    const currentEmailAddresses = (
      selectedUser as User & { email_addresses?: UserEmailAddress[] }
    ).email_addresses;

    if (
      emailAddressesSignature(currentEmailAddresses) ===
      emailAddressesSignature(userEmailAddressesCrud.data)
    ) {
      return;
    }

    dispatch(
      BasketballUsersSlice.basketballUsersActions.setUserEmailAddresses({
        userId: selectedUser.id,
        emailAddresses: userEmailAddressesCrud.data,
      })
    );
  }, [dispatch, selectedUser?.id, userEmailAddressesCrud.data]);

  const deleteTermination = () => {
    if (!selectedUser?.active_termination) {
      return;
    }

    setSaving(true);
    dispatch(UsersSlice.deleteUserTermination(selectedUser.active_termination))
      .unwrap()
      .catch(() => null)
      .finally(() => setSaving(false));
  };

  const userTerminationContent = useMemo(() => {
    const activeTermination = selectedUser?.active_termination;
    let text = t('termination.info.none');
    if (!!activeTermination) {
      text = t('termination.info.active', {
        date: formatDate(activeTermination.termination_date ?? ''),
        reason: activeTermination.reason,
      });
    }

    if (!selectedUser) {
      return null;
    }

    return (
      <Row>
        <Space direction="vertical">
          <Col span={24}>
            <Typography.Text>{text}</Typography.Text>
          </Col>
          {!!activeTermination ? (
            <Popconfirm
              title={t('termination.revoke')}
              onConfirm={() => {
                deleteTermination();
              }}
              okButtonProps={{
                loading: isSaving,
              }}
            >
              <Button loading={isSaving} icon={<DeleteOutlined />}>
                {t('termination.revoke')}
              </Button>
            </Popconfirm>
          ) : (
            <AddEditTerminationForm user={selectedUser} />
          )}
        </Space>
      </Row>
    );
  }, [selectedUser?.active_termination, isSaving]);

  return (
    <Tabs defaultActiveKey="1">
      <Tabs.TabPane key="1" tab={t('edit_user')}>
        <AddEditUserForm user={selectedUser} showSaveButton />
      </Tabs.TabPane>
      <Tabs.TabPane key="2" tab={t('roles.roles')}>
        <EditUserRolesForm user={selectedUser!} />
      </Tabs.TabPane>
      <Tabs.TabPane
        key="email_addresses"
        tab={t('users.email_addresses.plural')}
      >
        {selectedUser ? (
          <CrudPage
            resource={UserEmailAddressCrud}
            params={{
              userId: selectedUser.id,
            }}
            description={t('users.email_addresses.description')}
          />
        ) : null}
      </Tabs.TabPane>
      <Tabs.TabPane key="termination" tab={t('termination.singular')}>
        {userTerminationContent}
      </Tabs.TabPane>
      {(clubHasFirstSubscription || userIsAdmin) && (
        <Tabs.TabPane
          key="3"
          disabled={!clubHasFirstSubscription}
          tab={
            clubHasFirstSubscription ? (
              t('linked_accounts')
            ) : (
              <Tooltip title={t('errors.permissions.first')}>
                {t('linked_accounts')}
              </Tooltip>
            )
          }
        >
          <LinkedUserAccountsForm user={selectedUser!} />
        </Tabs.TabPane>
      )}
    </Tabs>
  );
}
